import React from "react";
import PropTypes from "prop-types";
import { Row, Col, Form } from "react-bootstrap";
import { SingleDatePicker } from "react-dates";
import { isWeekend } from "date-fns";
import { OrderStatus } from "../storeHome/store/constants";

const AdvancedBionicsOrderConfirmation = ({
  profileData,
  isStockOrder,
  siteList,
  fitterName,
  onFitterNameChange,
  firstName,
  onFirstNameChange,
  lastName,
  onLastNameChange,
  dateOfBirth,
  onDateOfBirthChange,
  onDateOfBirthPaste,
  requiredByDate,
  onRequiredByDateChange,
  requiredByDateFocused,
  onRequiredByDateFocusChange,
  onRequiredByDatePaste,
  minDate,
  invoiceTo,
  onInvoiceToChange,
  implantShipTo,
  onImplantShipToChange,
  switchOnKitShipTo,
  onSwitchOnKitShipToChange,
}) => (
  <Row>
    <Col xs={12} className="order-confirmation-sub-heading">
      <h2>Processing Information</h2>
    </Col>
    <Col xs={5}>
      <table className="table-full-width">
        <tbody>
          {profileData && profileData.FirstName && profileData.LastName && (
            <tr>
              <th>Name</th>
              <td></td>
              <td>{profileData.FirstName} {profileData.LastName}</td>
            </tr>
          )}
          {profileData && profileData.OrganisationCode && (
            <tr>
              <th>Organisation</th>
              <td></td>
              <td>{profileData.OrganisationName}</td>
            </tr>
          )}
          <tr>
            <th>Clinician Name *</th>
            <td></td>
            <td>
              <Form.Control
                type="text"
                name="fitterName"
                className="me-2"
                aria-label="clinicianName"
                onChange={onFitterNameChange}
                value={fitterName}
                required
                readOnly={isStockOrder === OrderStatus.STOCK_ORDER}
              />
            </td>
          </tr>
          {siteList && siteList.length === 1 && (
            <tr>
              <th>Site</th>
              <td></td>
              <td>{siteList[0] && siteList[0].SiteName}</td>
            </tr>
          )}
          <tr>
            <th>Client First Name *</th>
            <td></td>
            <td>
              <Form.Control
                type="text"
                name="firstName"
                className="me-2"
                aria-label="firstName"
                required
                onChange={onFirstNameChange}
                value={firstName}
                readOnly={isStockOrder === OrderStatus.STOCK_ORDER}
              />
            </td>
          </tr>
          <tr>
            <th>Client Surname *</th>
            <td></td>
            <td>
              <Form.Control
                type="text"
                name="lastName"
                className="me-2"
                aria-label="lastName"
                required
                onChange={onLastNameChange}
                value={lastName}
                readOnly={isStockOrder === OrderStatus.STOCK_ORDER}
              />
            </td>
          </tr>
          {isStockOrder !== OrderStatus.STOCK_ORDER && (
            <tr>
              <th>DOB</th>
              <td></td>
              <td>
                <Form.Control
                  type="date"
                  name="dateOfBirth"
                  className="me-2"
                  aria-label="dateOfBirth"
                  value={dateOfBirth || ""}
                  onChange={onDateOfBirthChange}
                  onPaste={onDateOfBirthPaste}
                />
              </td>
            </tr>
          )}
          <tr>
            <th>Required By Date *</th>
            <td></td>
            <td>
              <div onPaste={onRequiredByDatePaste} style={{ paddingBottom: "10px" }}>
                <SingleDatePicker
                  date={requiredByDate}
                  onDateChange={onRequiredByDateChange}
                  focused={requiredByDateFocused}
                  onFocusChange={({ focused }) => onRequiredByDateFocusChange(focused)}
                  id="advancedBionicsRequiredByDate"
                  isDayBlocked={(day) => isWeekend(day.toDate())}
                  numberOfMonths={1}
                  isOutsideRange={(day) => day.isBefore(minDate)}
                />
              </div>
              <span className="hafs-title">
                If an Implant and Switch on Kit are ordered together, both will be shipped on the same required date.
              </span>
            </td>
          </tr>
          <tr>
            <th>PO Number</th>
            <td></td>
            <td>
              <Form.Control
                type="text"
                name="purchaseOrderNumber"
                className="me-2"
                aria-label="purchaseOrderNumber"
              />
            </td>
          </tr>
          <tr>
            <th>Invoice To</th>
            <td></td>
            <td>
              <Form.Control as="select" name="invoiceTo" value={invoiceTo} onChange={onInvoiceToChange}>
                <option value="">Please select</option>
                <option value="MoH">MoH</option>
                <option value="ACC">ACC</option>
                <option value="SX">SX</option>
                <option value="Private Clinic">Private Clinic</option>
              </Form.Control>
            </td>
          </tr>
          <tr>
            <th>Ship To – Implant</th>
            <td></td>
            <td>
              <Form.Control as="textarea" rows={2} name="implantShipTo" value={implantShipTo} onChange={onImplantShipToChange} />
            </td>
          </tr>
          <tr>
            <th>Ship To – Switch on Kit / Externals</th>
            <td></td>
            <td>
              <Form.Control as="textarea" rows={2} name="switchOnKitShipTo" value={switchOnKitShipTo} onChange={onSwitchOnKitShipToChange} />
            </td>
          </tr>
          <tr>
            <td><p>*Required Field</p></td>
          </tr>
        </tbody>
      </table>
    </Col>
  </Row>
);

AdvancedBionicsOrderConfirmation.propTypes = {
  profileData: PropTypes.object,
  isStockOrder: PropTypes.string,
  siteList: PropTypes.array,
  fitterName: PropTypes.string,
  onFitterNameChange: PropTypes.func.isRequired,
  firstName: PropTypes.string,
  onFirstNameChange: PropTypes.func.isRequired,
  lastName: PropTypes.string,
  onLastNameChange: PropTypes.func.isRequired,
  dateOfBirth: PropTypes.string,
  onDateOfBirthChange: PropTypes.func.isRequired,
  onDateOfBirthPaste: PropTypes.func.isRequired,
  requiredByDate: PropTypes.object,
  onRequiredByDateChange: PropTypes.func.isRequired,
  requiredByDateFocused: PropTypes.bool,
  onRequiredByDateFocusChange: PropTypes.func.isRequired,
  onRequiredByDatePaste: PropTypes.func.isRequired,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  invoiceTo: PropTypes.string,
  onInvoiceToChange: PropTypes.func.isRequired,
  implantShipTo: PropTypes.string,
  onImplantShipToChange: PropTypes.func.isRequired,
  switchOnKitShipTo: PropTypes.string,
  onSwitchOnKitShipToChange: PropTypes.func.isRequired,
};

export default AdvancedBionicsOrderConfirmation;
